// A country's flag, drawn inline (UI-8). Windows draws no flag emoji — a
// regional-indicator pair comes out as two letters — so every flag the admin
// shows is a small SVG of its own, here.
//
// The colours are the countries', not the admin's: they are the one place in
// the admin's sources that holds colour literals, and the structure test
// names this file as its exception. A code without a drawing (INT, a new
// country) shows as its code on a neutral tag rather than as a wrong flag.

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

const W = 30;
const H = 20;

function horizontal(colours: string[]): ReactNode[] {
  const step = H / colours.length;
  return colours.map((fill, index) => (
    <rect key={`h${index}`} x={0} y={index * step} width={W} height={step + 0.05} fill={fill} />
  ));
}

function vertical(colours: string[]): ReactNode[] {
  const step = W / colours.length;
  return colours.map((fill, index) => (
    <rect key={`v${index}`} x={index * step} y={0} width={step + 0.05} height={H} fill={fill} />
  ));
}

function greece(): ReactNode[] {
  const step = H / 9;
  const stripes = Array.from({ length: 9 }, (_, index) => (
    <rect
      key={`g${index}`}
      x={0}
      y={index * step}
      width={W}
      height={step + 0.05}
      fill={index % 2 === 0 ? "#0D5EAF" : "#FFFFFF"}
    />
  ));
  const side = step * 5;
  return [
    ...stripes,
    <rect key="canton" x={0} y={0} width={side} height={side} fill="#0D5EAF" />,
    <rect key="cross-v" x={side / 2 - 1.1} y={0} width={2.2} height={side} fill="#FFFFFF" />,
    <rect key="cross-h" x={0} y={side / 2 - 1.1} width={side} height={2.2} fill="#FFFFFF" />,
  ];
}

function europe(): ReactNode[] {
  const stars = Array.from({ length: 12 }, (_, index) => {
    const angle = (2 * Math.PI * index) / 12;
    return (
      <circle
        key={`s${index}`}
        cx={15 + 6 * Math.cos(angle)}
        cy={10 + 6 * Math.sin(angle)}
        r={0.95}
        fill="#FFCC00"
      />
    );
  });
  return [<rect key="field" width={W} height={H} fill="#003399" />, ...stars];
}

function croatia(): ReactNode[] {
  const squares = Array.from({ length: 12 }, (_, index) => (
    <rect
      key={`c${index}`}
      x={12 + (index % 3) * 2}
      y={6 + Math.floor(index / 3) * 2}
      width={2}
      height={2}
      fill={((index % 3) + Math.floor(index / 3)) % 2 === 0 ? "#FF0000" : "#FFFFFF"}
    />
  ));
  return [
    ...horizontal(["#FF0000", "#FFFFFF", "#171796"]),
    ...squares,
    <rect key="frame" x={12} y={6} width={6} height={8} fill="none" stroke="#171796" strokeWidth={0.5} />,
  ];
}

const FLAGS: Record<string, () => ReactNode[]> = {
  RO: () => vertical(["#002B7F", "#FCD116", "#CE1126"]),
  PL: () => horizontal(["#FFFFFF", "#DC143C"]),
  HU: () => horizontal(["#CE2939", "#FFFFFF", "#477050"]),
  GR: greece,
  SK: () => [
    ...horizontal(["#FFFFFF", "#0B4EA2", "#EE1C25"]),
    <path
      key="shield"
      d="M7 5.2h6v5.6c0 2.2-1.6 3.3-3 3.9-1.4-.6-3-1.7-3-3.9z"
      fill="#EE1C25"
      stroke="#FFFFFF"
      strokeWidth={0.8}
    />,
    <path key="cross" d="M10 6.6v6.2M8.6 8.4h2.8M8.2 10.1h3.6" stroke="#FFFFFF" strokeWidth={0.8} />,
  ],
  EU: europe,
  BG: () => horizontal(["#FFFFFF", "#00966E", "#D62612"]),
  CZ: () => [
    ...horizontal(["#FFFFFF", "#D7141A"]),
    <path key="wedge" d="M0 0 15 10 0 20z" fill="#11457E" />,
  ],
  IT: () => vertical(["#009246", "#FFFFFF", "#CE2B37"]),
  HR: croatia,
  NL: () => horizontal(["#AE1C28", "#FFFFFF", "#21468B"]),
  SI: () => [
    ...horizontal(["#FFFFFF", "#005DA4", "#ED1C24"]),
    <path
      key="shield"
      d="M6 3.5h5v4.4c0 1.8-1.3 2.6-2.5 3.1C7.3 10.5 6 9.7 6 7.9z"
      fill="#005DA4"
      stroke="#ED1C24"
      strokeWidth={0.6}
    />,
  ],
};

/** Whether the admin can draw this code's flag. */
export function hasFlag(code: string): boolean {
  return code.toUpperCase() in FLAGS;
}

interface FlagProps {
  code: string;
  /** A round flag, for stacks of countries in a narrow cell. */
  round?: boolean;
  className?: string;
}

/** The flag alone. Decorative: the code is always written beside it, or
 * in its title for a round stack. */
export function Flag({ code, round = false, className }: FlagProps) {
  const draw = FLAGS[code.toUpperCase()];
  if (draw === undefined) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex h-3.5 shrink-0 items-center rounded-flag bg-fill px-1 text-[0.625rem] leading-none font-semibold text-muted-foreground",
          round && "size-5 justify-center rounded-full px-0",
          className,
        )}
      >
        {round ? code.slice(0, 2) : code}
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block shrink-0 overflow-hidden align-[-2px] ring-1 ring-line-strong/60",
        round ? "size-5 rounded-full" : "h-3.5 w-5.25 rounded-flag",
        className,
      )}
    >
      <svg
        viewBox={round ? "5 0 20 20" : `0 0 ${W} ${H}`}
        preserveAspectRatio={round ? "xMidYMid slice" : "none"}
        className="block size-full"
      >
        {draw()}
      </svg>
    </span>
  );
}

/** A country as the tables show it: the flag and the code, semibold. */
export function Country({
  code,
  className,
  children,
}: {
  code: string | null | undefined;
  className?: string;
  children?: ReactNode;
}) {
  if (!code) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={cn("inline-flex items-center gap-1.5 font-semibold whitespace-nowrap", className)} title={code}>
      <Flag code={code} />
      {children ?? code}
    </span>
  );
}

/** Several countries in a narrow place: overlapping round flags, the rest as
 * "+n". The codes are in the title and for screen readers. */
export function FlagStack({ codes, max = 4, className }: { codes: string[]; max?: number; className?: string }) {
  const shown = codes.slice(0, max);
  const rest = codes.length - shown.length;
  return (
    <span className={cn("inline-flex items-center", className)} title={codes.join(", ")}>
      <span className="sr-only">{codes.join(", ")}</span>
      {shown.map((code, index) => (
        <Flag key={code} code={code} round className={cn("ring-2 ring-card", index > 0 && "-ml-1")} />
      ))}
      {rest > 0 && <span className="ml-1 text-label font-semibold text-muted-foreground">+{rest}</span>}
    </span>
  );
}
